import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';

interface NavigationStatusProps {
  step: number;
  locationState?: any; // State to preserve when navigating back
}

function NavigationStatus({ step, locationState }: NavigationStatusProps): JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const isRemovalsFlow = location.pathname.includes('/removals');
  const isSuitcaseFlow = location.pathname.includes('/suitcase');
  const isStorageFlow = location.pathname.includes('/storage');
  const isShippingFlow = location.pathname.includes('/shipping');

  const steps: JSX.Element[] = [];
  const stepLimit = isRemovalsFlow ? 6 : 4; // 5 numbered steps + payment for removals, 3 numbered steps + payment for storage
  const maxNumberedSteps = isRemovalsFlow ? 5 : 3;

  // Step descriptions for removals flow
  const removalStepDescriptions = [
    'Select your move type',
    'More info about your type of move',
    'Storage options',
    'Moving Addresses',
    'Payment'
  ];

  // Function to get the route for a specific step
  const getRouteForStep = (stepNumber: number): string | null => {
    if (isRemovalsFlow) {
      switch (stepNumber) {
        case 1: return '/removals';
        case 2: return '/removals/questions';
        case 3: return '/removals/extras';
        case 4: return '/removals/collection_address';
        case 5: return '/removals/payment';
        default: return null;
      }
    } else {
      // Storage, Shipping, or Suitcase flow
      let baseRoute = '';
      if (isStorageFlow) baseRoute = '/storage';
      else if (isShippingFlow) baseRoute = '/shipping';
      else if (isSuitcaseFlow) baseRoute = '/suitcase';
      
      if (!baseRoute) return null;
      
      switch (stepNumber) {
        case 1: return `${baseRoute}/boxes`;
        case 2: return `${baseRoute}/collection_address`;
        case 3: return `${baseRoute}/payment`;
        default: return null;
      }
    }
  };

  // Function to handle step click
  const handleStepClick = (stepNumber: number) => {
    // Only allow navigation to current or previous steps
    if (stepNumber >= step) return;
    
    const route = getRouteForStep(stepNumber);
    if (route) {
      // Preserve the state when navigating back
      history.push(route, locationState || location.state);
    }
  };

  for (let i = 1; i <= stepLimit; i++) {
    const active = 'indicator-line active';
    const inactive = 'indicator-line';

    let className = step >= i ? active : inactive;
    if (i === maxNumberedSteps && step <= maxNumberedSteps) {
      className += ' no-active-after';
    }

    if (i === stepLimit) continue; // Skip the last step (payment box)

    const isClickable = i < step; // Can only click on previous steps
    const spanClassName = step >= i ? 'active' : undefined;
    const clickableClass = isClickable ? 'clickable' : '';

    steps.push(
      <div key={i} className={`${className} ${clickableClass}`}>
        <span 
          className={spanClassName}
          onClick={() => isClickable && handleStepClick(i)}
          style={{ cursor: isClickable ? 'pointer' : 'default' }}
        >
          {i}
        </span>
      </div>,
    );
  }

  return (
    <>
      <div className="App-numerical-indicator">
        {steps.filter((_, i) => i < maxNumberedSteps)}
        <div className="indicator-line box-indicator">
          <span className="box">
            <img src="/img/box-icon-white.png" alt="" />
          </span>
        </div>
      </div>

      {isRemovalsFlow && (
        <div className="navigation-step-labels">
          {removalStepDescriptions.map((description, index) => {
            const stepNumber = index + 1;
            const isActive = step >= stepNumber;
            const isClickable = stepNumber < step;
            const labelClassName = `step-label ${isActive ? 'active' : ''} ${isClickable ? 'clickable' : ''}`;

            return (
              <div
                key={stepNumber}
                className={labelClassName}
                onClick={() => isClickable && handleStepClick(stepNumber)}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
              >
                {description}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default NavigationStatus;
